import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { dateFormatter, reportContentFormatter, reportDateFormatter, reportHourFormatter, reportKeyTypeFormatter, staffRoleFormatter } from '../../../lib/formatter';

const ReportDetail = ({
  isOpen,
  toggle,
  reportItem,
  reportType,
}) => {
  const { t } = useTranslation();
  const reportColumn = {
    userkey: [
      { label: t('data_column.key_type'), key: 'key_type' },
      { label: t('data_column.content'), key: 'operation' },
      { label: t('data_column.building'), key: 'building_name' },
      { label: t('data_column.room'), key: 'json_destination.room' },
      { label: t('data_column.user_name'), key: 'grantee_name' },
      { label: t('data_column.phone_number'), key: 'grantee_phone_number' },
      { label: t('data_column.checkin_day'), key: 'start_at_day' },
      { label: t('data_column.checkin_hour'), key: 'start_at_hour' },
      { label: t('data_column.checkout_day'), key: 'end_at_day' },
      { label: t('data_column.checkout_hour'), key: 'end_at_hour' },
      { label: t('data_column.staff'), key: 'actor_name' },
      { label: t('data_column.sequence'), key: 'json_context.seq_num' },
      { label: t('data_column.created_at'), key: 'created_at' },
    ],
    facility: [
      { label: t('data_column.facility'), key: 'facility_name' },
      { label: t('data_column.building'), key: 'building_name', hidden: ['hotel', 'building'].includes(reportItem.report_subtype) },
      { label: t('data_column.content'), key: 'operation' },
      { label: t('data_column.content_detail'), key: 'json_diffs', hidden: !reportItem.json_diffs },
      { label: t('data_column.staff'), key: 'actor_name' },
      { label: t('data_column.created_at'), key: 'created_at' },
    ],
    staff: [
      { label: t('data_column.actor_name'), key: 'actor_name' },
      { label: t('data_column.actor_phone_number'), key: 'actor_phone_number' },
      { label: t('data_column.actor_role'), key: 'actor_role' },
      { label: t('data_column.content'), key: 'operation' },
      { label: t('data_column.key_type'), key: 'key_type', hidden: !reportItem.key_type },
      { label: t('data_column.content_detail'), key: 'json_diffs', hidden: !reportItem.json_diffs },
      { label: t('data_column.room'), key: 'json_destination.rooms', hidden: !reportItem.json_destination },
      { label: t('data_column.owner_name'), key: 'grantee_name' },
      { label: t('data_column.owner_phone_number'), key: 'grantee_phone_number' },
      { label: t('data_column.owner_role'), key: 'grantee_role' },
      { label: t('data_column.created_at'), key: 'created_at' },
    ],
    user_mobilekey: [
      { label: t('data_column.building'), key: 'building_name' },
      { label: t('data_column.room'), key: 'json_destination.room' },
      { label: t('data_column.content'), key: 'operation' },
      { label: t('data_column.key_user_name'), key: 'actor_name' },
      { label: t('data_column.phone_number'), key: 'actor_phone_number' },
      { label: t('data_column.created_at'), key: 'created_at' },
    ],
  };

  const formatData = (reportItem, key) => {
    switch (key) {
      case 'operation':
        return reportContentFormatter(reportItem.operation);
      case 'key_type':
        return reportKeyTypeFormatter(reportItem.key_type);
      case 'actor_role':
        return staffRoleFormatter(reportItem.actor_role);
      case 'grantee_role':
        return staffRoleFormatter(reportItem.grantee_role);
      case 'start_at_day':
        return reportItem.json_context ?
          reportDateFormatter(reportItem.json_context.start_at) : '-';
      case 'start_at_hour':
        return reportItem.json_context ?
          reportHourFormatter(reportItem.json_context.start_at) : '-';
      case 'end_at_day':
        return reportItem.json_context ?
          reportDateFormatter(reportItem.json_context.end_at) : '-';
      case 'end_at_hour':
        return reportItem.json_context ?
          reportHourFormatter(reportItem.json_context.end_at) : '-';
      case 'json_destination.room':
        return reportItem.json_destination ? reportItem.json_destination.room : '-';
      case 'json_destination.rooms':
        return reportItem.json_destination ? reportItem.json_destination.rooms : '-';
      case 'json_context.seq_num':
        return reportItem.json_context ? reportItem.json_context.seq_num : '-';
      case 'json_diffs':
        return reportItem.json_diffs ? jsonDiffsFormatter(reportItem.json_diffs) : '-';
      case 'created_at':
        return dateFormatter(reportItem.created_at);
      default:
        return reportItem[key] || '-';
    };
  };

  const jsonDiffsFormatter = (diffs) => {
    return (
      <span>
        {diffs.map((diff, index) =>
          <Fragment>
            <span key={index}>
              {diff.key + ': ' + diff.oldValue + ' -> ' + diff.newValue}
            </span><br/>
          </Fragment>
        )}
      </span>
    );
  };

  return (
    <Fragment>
      <Modal
        show={isOpen}
        onHide={() => toggle()}
        centered
        size='md'
      >
        <ModalBody className='text-center m-3' style={{margin:0, padding:0, backgroundColor:'#282C34'}}>
          <div className='form-card'>
            <div className='form-card-title'>
              <h4>{t('content_title.report_detail')}</h4>
            </div>
            <div className='inline-flex'>
              <div className='w-100'>
                {reportColumn[reportType].map((column, index) => (
                  <div className='card-text' key={index} hidden={column.hidden}>
                    <p>
                      <b>{column.label}</b>
                    </p>
                    <p className='card-text-display'>
                      {formatData(reportItem, column.key)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className='card-foot center'>
              <div className='form-item-close'>
                <button className='btn-item-close' onClick={() => toggle()}>
                  <FontAwesomeIcon icon={faTimesCircle} style={{paddingRight:'3px'}}/><span>{t('button.close')}</span>
                </button>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default ReportDetail;